import type { ReactNode } from 'react';
import { cn } from '../../../lib/utils';
import { Button, type ButtonProps } from '../../ui/button';
import { DisabledReasonTooltip } from '../../ui/tooltip/disabled-reason-tooltip';

export interface FormFooterProps {
  onBack?: () => void;
  onNext: () => void;
  backLabel?: string;
  nextLabel?: string;
  loading?: boolean;
  backDisabled?: boolean;
  nextDisabled?: boolean;
  /**
   * Why "next" is blocked. With `nextDisabled`, the button stays focusable and
   * clickable (`aria-disabled`, same visual as disabled) and reveals this reason
   * in a tooltip instead of calling `onNext` — Figma "click disabled button".
   * Ignored while `loading` (the button is really disabled then).
   */
  nextDisabledReason?: string;
  /** Same as `nextDisabledReason`, for the back button. */
  backDisabledReason?: string;
  className?: string;
  /** Replaces the default back button with a custom element */
  backSlot?: ReactNode;
  /**
   * Optional content rendered BETWEEN the back and next buttons (desktop) and
   * ABOVE them, full-width, on mobile (e.g. a checkbox the design places in
   * the sticky footer).
   */
  centerSlot?: ReactNode;
  /** Root testid — back/next buttons get `<testid>-back` and `<testid>-next`. */
  'data-testid'?: string;
}

interface DisabledReasonButtonProps extends Omit<ButtonProps, 'disabled' | 'onClick' | 'loading'> {
  reason: string;
}

/**
 * A "soft-disabled" button: looks disabled, never fires its action, but keeps
 * focus/click so the blocking reason can be surfaced (tooltip). The tooltip
 * mechanics live in the shared `DisabledReasonTooltip`.
 */
function DisabledReasonButton({
  reason,
  className,
  variant,
  children,
  ...props
}: DisabledReasonButtonProps) {
  return (
    <DisabledReasonTooltip reason={reason}>
      <Button
        {...props}
        variant={variant}
        className={cn(
          'cursor-not-allowed opacity-50',
          variant === 'outline' ? 'hover:bg-background hover:text-foreground' : 'hover:bg-primary',
          className
        )}
      >
        {children}
      </Button>
    </DisabledReasonTooltip>
  );
}
DisabledReasonButton.displayName = 'DisabledReasonButton';

function FormFooter({
  onBack,
  onNext,
  backLabel = 'Voltar',
  nextLabel = 'Avançar',
  loading = false,
  backDisabled = false,
  nextDisabled = false,
  nextDisabledReason,
  backDisabledReason,
  className,
  backSlot,
  centerSlot,
  'data-testid': testId,
}: FormFooterProps) {
  const backTestId = testId ? `${testId}-back` : undefined;
  const nextTestId = testId ? `${testId}-next` : undefined;
  const softDisabledBack = backDisabled && Boolean(backDisabledReason) && !loading;
  const softDisabledNext = nextDisabled && Boolean(nextDisabledReason) && !loading;

  return (
    <div
      data-slot="form-footer"
      data-testid={testId}
      className={cn(
        'flex w-full items-center justify-between gap-4',
        centerSlot != null && 'flex-wrap gap-y-3',
        className
      )}
    >
      {backSlot ??
        (onBack ? (
          softDisabledBack ? (
            <DisabledReasonButton
              type="button"
              variant="outline"
              reason={backDisabledReason as string}
              data-testid={backTestId}
            >
              {backLabel}
            </DisabledReasonButton>
          ) : (
            <Button
              type="button"
              variant="outline"
              onClick={onBack}
              disabled={backDisabled}
              data-testid={backTestId}
            >
              {backLabel}
            </Button>
          )
        ) : (
          <span />
        ))}
      {centerSlot != null && (
        <div
          data-slot="form-footer-center"
          className="order-first flex w-full justify-start sm:order-none sm:w-auto sm:flex-1 sm:justify-center"
        >
          {centerSlot}
        </div>
      )}
      {softDisabledNext ? (
        <DisabledReasonButton
          type="button"
          reason={nextDisabledReason as string}
          data-testid={nextTestId}
        >
          {nextLabel}
        </DisabledReasonButton>
      ) : (
        <Button
          type="button"
          onClick={onNext}
          disabled={nextDisabled}
          loading={loading}
          data-testid={nextTestId}
        >
          {nextLabel}
        </Button>
      )}
    </div>
  );
}
FormFooter.displayName = 'FormFooter';

export { FormFooter };
